import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, Text, View } from 'react-native';

import { lowerDisplayLineTestIDs, Theme } from '@constants';
import { useShowNextLDLProduct, useThreeDayForecast } from '@hooks';

import { formatPrecipChance } from '../../../helpers/DataFormatting';
import { isAndroid } from '../../../helpers/Platform';
import { TWCAnimatedIcon } from '../../primitives/TWCAnimatedIcon/TWCAnimatedIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { determineProductLabel } from '../determineProductLabel';

const testIDPath = lowerDisplayLineTestIDs.threeDay;
const LDLThreeDayAPI = () => {
  const data = useThreeDayForecast();
  const showNextProduct = useShowNextLDLProduct();

  useEffect(() => {
    data.length === 0 && showNextProduct(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return data.length > 0 ? <LDLThreeDayUI data={data} onFinish={showNextProduct} /> : <></>;
};

const LDLThreeDayUI = ({ data, onFinish }) => {
  const precipAnimation = useRef(new Animated.Value(isAndroid ? Theme.px(-34) : Theme.px(-33))).current;
  const finalShowValue = useRef(new Animated.Value(0)).current;

  const internalAnimation = Animated.timing(precipAnimation, {
    delay: 5000,
    toValue: isAndroid ? Theme.px(-128) : Theme.px(-125),
    duration: 500,
    useNativeDriver: true,
  });
  const finalShow = Animated.timing(finalShowValue, {
    duration: 5000,
    useNativeDriver: true,
  });
  const { dayOfWeekToday, daypartName } = data[0];

  // TODO: may have to change implementation when integrating TwoDay and ThreeDay
  const animationSequence = data ? [internalAnimation] : [];

  useEffect(() => {
    const animation = Animated.sequence([...animationSequence, finalShow]);

    animation.start(({ finished }) => finished && onFinish?.());
    return animation.reset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={[styles.productWrapper]}>
      <View>
        <TWCText style={styles.productText} testID={testIDPath.productLabel} variant={'condensedText36Transparent'}>
          {dayOfWeekToday && daypartName ? determineProductLabel(dayOfWeekToday, daypartName)[0] : ''}
        </TWCText>
      </View>
      <View style={styles.row}>
        {data.map(day => {
          const { iconCodeExtend, temperatureHigh, temperatureLow, dayAbbreviation, precipChance, key } = day;

          const precipChanceFormatted = formatPrecipChance(precipChance);

          return (
            <View key={`${key}`} style={styles.forecasts}>
              <TWCText style={styles.dayOfWeek} testID={testIDPath.dayOfWeek} variant={'condensedText36Transparent'}>
                {dayAbbreviation ?? ''}
              </TWCText>
              <TWCAnimatedIcon code={iconCodeExtend} />
              <View style={styles.itemText}>
                <Text style={styles.sectionEnd}>
                  <TWCText testID={testIDPath.temperatureHigh} variant={'condensedText36'}>
                    {temperatureHigh ? `${temperatureHigh}°` : ''}
                    {temperatureHigh && temperatureLow ? '/' : ''}
                  </TWCText>
                  <View>
                    <Animated.View
                      style={[
                        styles.animatedWrapper,
                        {
                          transform: [{ translateY: precipAnimation }],
                        },
                      ]}
                      useNativeDriver
                    >
                      <TWCText
                        style={styles.temperatureLow}
                        testID={testIDPath.temperatureLow}
                        variant={'condensedText36'}
                      >
                        {temperatureLow ? `${temperatureLow}°` : ''}
                      </TWCText>
                      <TWCText
                        style={[
                          styles.precipChance,
                          {
                            transform: [{ translateY: Theme.px(50) }],
                          },
                        ]}
                        testID={testIDPath.precipChance}
                        variant={'condensedText36'}
                      >
                        {precipChanceFormatted}
                      </TWCText>
                    </Animated.View>
                  </View>
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

export const LDLThreeDay = {
  API: LDLThreeDayAPI,
  UI: LDLThreeDayUI,
};

const styles = StyleSheet.create({
  animatedWrapper: {
    position: 'absolute',
  },
  dayOfWeek: {
    paddingRight: Theme.px(18),
  },
  forecasts: {
    alignItems: 'center',
    flexDirection: 'row',
    height: '100%',
  },
  itemText: {
    paddingLeft: Theme.px(18),
    paddingRight: Theme.px(73),
  },
  productText: {
    paddingRight: Theme.px(48),
  },
  productWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  row: {
    flexDirection: 'row',
  },
  sectionEnd: {
    paddingRight: Theme.px(35),
  },
  temperatureLow: {
    color: Theme.malibuBlue,
  },
});
